/**
 * ═══════════════════════════════════════════════════════════════
 * Hasımoğlu Çiftliği – Kurban Yönetim Platformu
 * ───────────────────────────────────────────────────────────────
 * Ana Uygulama Bileşeni — Rol Bazlı Yönlendirme
 *
 * Route Yapısı:
 *   /            → Tanıtım Sayfası (LandingPage)
 *   /login       → Giriş (GuestRoute)
 *   /katalog     → Ziyaretçi Kataloğu
 *   /dashboard   → Admin Paneli (superAdmin, admin)
 *   /hesabim     → Müşteri Paneli (customer)
 *   /yetkisiz    → Yetkisiz Erişim
 *   *            → 404 → Ana sayfaya yönlendir
 *
 * Disiplin Kuralları:
 *   - Finans silme/düzenleme: Sadece superAdmin
 *   - Alt yönetici (admin): Ödeme girişi var, silme/düzenleme yok
 *   - Müşteri: Sadece kendi verilerini görür
 * ═══════════════════════════════════════════════════════════════
 */

import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { GuestRoute, RoleRoute } from './components/routing/RouteGuards';
import { useAuth } from './context/AuthContext';

// ── Layouts ──
import DashboardLayout from './components/layout/DashboardLayout';

import LoginPage from './pages/auth/LoginPage';
import AdminDashboard from './pages/dashboard/AdminDashboard';
import AnimalManagement from './pages/dashboard/AnimalManagement';
import UserManagement from './pages/dashboard/UserManagement';
import ShareholderManagement from './pages/dashboard/ShareholderManagement';
import PaymentManagement from './pages/dashboard/PaymentManagement';
import TicketManagement from './pages/dashboard/TicketManagement';
import SystemSettings from './pages/dashboard/SystemSettings';
import CustomerDashboard from './pages/customer/CustomerDashboard';
import UnauthorizedPage from './pages/UnauthorizedPage';
import MaintenancePage from './pages/MaintenancePage';
import PublicCatalogPage from './pages/public/PublicCatalogPage';
import LandingPage from './pages/public/LandingPage';
import TrashManagement from './pages/dashboard/TrashManagement';
import DeliveryManagement from './pages/dashboard/DeliveryManagement';

export default function App() {
  const { isSuperAdmin, systemSettings, currentUser } = useAuth();

  // Bakım modu kontrolü: Login sayfasını HER ZAMAN erişilebilir bırak
  // Böylece SuperAdmin bakım modundayken bile giriş yapabilir
  const isMaintenanceActive = systemSettings?.isSystemMaintenance && !isSuperAdmin;

  return (
    <Routes>
      {/* ── Misafir (Login) – BAKIM MODUNDA DAHİ ERİŞİLEBİLİR ── */}
      <Route
        path="/login"
        element={
          <GuestRoute>
            <LoginPage />
          </GuestRoute>
        }
      />

      {/* ── Admin Dashboard (superAdmin + admin + subAdmin) ── */}
      <Route
        path="/dashboard"
        element={
          <RoleRoute allowedRoles={['superAdmin', 'admin', 'subAdmin']}>
            <DashboardLayout />
          </RoleRoute>
        }
      >
        <Route index element={<AdminDashboard />} />
        <Route path="kurbanlar" element={<AnimalManagement />} />
        <Route path="hissedarlar" element={<ShareholderManagement />} />
        <Route path="tahsilat" element={<PaymentManagement />} />
        <Route path="sms" element={<div className="p-8 text-white"><h1 className="text-2xl font-bold">SMS Gönderim (Yapım Aşamasında)</h1></div>} />
        <Route path="mesajlar" element={<TicketManagement />} />
        <Route 
          path="adminler" 
          element={
            <RoleRoute allowedRoles={['superAdmin', 'admin']}>
              <UserManagement />
            </RoleRoute>
          } 
        />
        <Route 
          path="ayarlar" 
          element={
            <RoleRoute allowedRoles={['superAdmin']}>
              <SystemSettings />
            </RoleRoute>
          } 
        />
        <Route
          path="cop-kutusu"
          element={
            <RoleRoute allowedRoles={['superAdmin']}>
              <TrashManagement />
            </RoleRoute>
          }
        />
        <Route
          path="teslimat"
          element={
            <RoleRoute allowedRoles={['superAdmin']}>
              <DeliveryManagement />
            </RoleRoute>
          }
        />
      </Route>

      {/* ── Müşteri Paneli (Bakım modunda kapalı) ── */}
      <Route
        path="/hesabim"
        element={
          isMaintenanceActive 
            ? <MaintenancePage /> 
            : <RoleRoute allowedRoles={['customer']}><CustomerDashboard /></RoleRoute>
        }
      />

      {/* ── Yetkisiz Erişim ── */}
      <Route path="/yetkisiz" element={<UnauthorizedPage />} />

      {/* ── Dışa Açık (Public) Katalog (Bakım modunda kapalı) ── */}
      <Route path="/katalog" element={isMaintenanceActive ? <MaintenancePage /> : <PublicCatalogPage />} />

      {/* ── Ana Sayfa (Tanıtım / Landing Page) ── */}
      <Route path="/" element={<LandingPage />} />

      {/* ── 404 → Ana Sayfaya Yönlendir ── */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
